import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import AuthLayout from '../../features/auth/AuthLayout';
import LogoHeader from '../../components/LogoHeader';
import OneVerseLogo from '../../components/OneVerseLogo';
import AuthCard from '../../features/auth/AuthCard';
import PasswordRequirements from '../../features/auth/PasswordRequirements';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/API';
import { PASSWORD_RULES } from '@/features/auth/registration/schema';

type Step = 'loading' | 'invalid' | 'request' | 'confirm';

const SetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('loading');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStep('invalid');
      return;
    }

    API.fetch('/auth/validate_setup_token', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
      .then(res => res.json())
      .then(data => {
        if (data.email) {
          setEmail(data.email);
          setStep('request');
        } else {
          setStep('invalid');
        }
      })
      .catch(() => setStep('invalid'));
  }, [searchParams]);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await API.fetch('/auth/forgot_password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else {
        setMessage(data.message);
        setStep('confirm');
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!confirmationCode || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (PASSWORD_RULES.some(r => !r.test(newPassword))) {
      setShowPasswordRequirements(true);
      return;
    }
    setShowPasswordRequirements(false);
    setLoading(true);

    try {
      const response = await API.fetch('/auth/confirm_forgot_password', {
        method: 'POST',
        body: JSON.stringify({
          email,
          confirmation_code: confirmationCode,
          new_password: newPassword,
        }),
      });
      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else {
        setMessage(data.message);
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'loading') {
    return (
      <main>
        <AuthLayout>
          <LogoHeader />
          <AuthCard>
            <OneVerseLogo className="mb-6 mt-2 mx-auto" />
            <p className="text-center text-gray-500">Verifying your link…</p>
          </AuthCard>
        </AuthLayout>
      </main>
    );
  }

  if (step === 'invalid') {
    return (
      <main>
        <AuthLayout>
          <LogoHeader />
          <AuthCard>
            <OneVerseLogo className="mb-6 mt-2 mx-auto" />
            <h1 className="text-center text-2xl font-semibold text-gray-900 mb-4">
              Invalid link
            </h1>
            <p className="text-center text-gray-500 mb-6">
              This link is invalid. Please contact support if you need help accessing your account.
            </p>
            <div className="text-center">
              <Link to="/login" className="font-medium text-blue-700 hover:underline">
                Go to login
              </Link>
            </div>
          </AuthCard>
        </AuthLayout>
      </main>
    );
  }

  return (
    <main>
      <AuthLayout>
        <LogoHeader />
        <AuthCard>
          <OneVerseLogo className="mb-6 mt-2 mx-auto" />
          <h1 className="text-center text-2xl font-semibold text-gray-900">
            {step === 'request' ? 'Set your password' : 'Enter your code'}
          </h1>
          <p className="text-center text-gray-500 mb-6">
            {step === 'request'
              ? 'Click below to receive a one-time code, then set your new password.'
              : 'Enter the code from your email and choose a new password.'}
          </p>

          <div className="w-full max-w-md mx-auto p-6">
            {step === 'request' ? (
              <form onSubmit={handleRequestCode} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-medium">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    readOnly
                    className="bg-blue-50 border-0 rounded-lg h-12 px-4 text-black"
                  />
                </div>

                {error && <p className="text-red-500 text-center">{error}</p>}
                {message && <p className="text-green-600 text-center">{message}</p>}

                <Button
                  type="submit"
                  className="w-full h-12 bg-indigo-700 hover:bg-indigo-800 text-white hover:text-white font-semibold rounded-lg text-base"
                  disabled={loading}
                >
                  {loading ? 'Sending…' : 'Send code'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleConfirm} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="confirmationCode" className="text-gray-700 font-medium">
                    Confirmation Code
                  </Label>
                  <Input
                    id="confirmationCode"
                    type="text"
                    value={confirmationCode}
                    onChange={e => setConfirmationCode(e.target.value)}
                    className="bg-blue-50 border-0 rounded-lg h-12 px-4 text-black"
                    placeholder="Enter the code from your email"
                    disabled={loading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-gray-700 font-medium">
                    New Password
                  </Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={e => {
                      setNewPassword(e.target.value);
                      setShowPasswordRequirements(false);
                    }}
                    className="bg-blue-50 border-0 rounded-lg h-12 px-4 text-black"
                    placeholder="Choose a password"
                    disabled={loading}
                  />
                  <PasswordRequirements show={showPasswordRequirements} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-gray-700 font-medium">
                    Confirm Password
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    className="bg-blue-50 border-0 rounded-lg h-12 px-4 text-black"
                    placeholder="Confirm your password"
                    disabled={loading}
                  />
                </div>

                {error && <p className="text-red-500 text-center">{error}</p>}
                {message && <p className="text-green-600 text-center">{message}</p>}

                <Button
                  type="submit"
                  className="w-full h-12 bg-indigo-700 hover:bg-indigo-800 text-white hover:text-white font-semibold rounded-lg text-base"
                  disabled={loading}
                >
                  {loading ? 'Setting password…' : 'Set password'}
                </Button>

                <Button
                  type="button"
                  onClick={() => setStep('request')}
                  variant="outline"
                  className="w-full h-12 border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg text-base"
                >
                  Resend code
                </Button>
              </form>
            )}

            <p className="mt-4 text-center text-sm text-gray-600">
              Already have a password?{' '}
              <Link to="/login" className="font-medium text-blue-700 hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </AuthCard>
      </AuthLayout>
    </main>
  );
};

export default SetPassword;
